import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api/api';
import type {
  NotificationPreferences,
  UpdateNotificationPreferencesInput,
} from '@/lib/api/domains/notification-preferences';
import type { UserId } from '@/lib/api/ids';
import { keys } from '@/lib/query-keys';

/*
 * Update-notification-preferences mutation (F02 R25/R26/R35). The seam the
 * preference screen's Save invokes. Mutate → invalidate: on success the user's
 * preferences are invalidated so the screen re-reads what is now stored. No
 * optimistic update — like every write, mutate then invalidate.
 *
 * The error is NOT swallowed: a whole-submission refusal for an unknown key
 * arrives as an `ApiError` (AC-8ddebd3b) carrying the unrecognized names
 * (AC-c7c5f9af); the screen reads it with `unrecognizedKeysFromError` and names
 * the keys where the person works, treating the save as failed — never success,
 * and nothing is shown as stored (the server changed nothing). The request
 * targets the account by id; the four keys are governed ids, not PII.
 *
 * The write goes through the transport (`api.notificationPreferences.update`),
 * the one network door; a 4xx surfaces as an `ApiError` through the shared
 * error middleware, never swallowed, so the screen can treat it as failed and
 * name the unrecognized keys.
 */
export function useUpdateNotificationPreferences(userId: UserId) {
  const queryClient = useQueryClient();
  return useMutation<
    NotificationPreferences,
    Error,
    UpdateNotificationPreferencesInput
  >({
    mutationFn: (input) => api.notificationPreferences.update(userId, input),
    onSuccess: () => {
      // Mutate → invalidate: the user's current choices AND their change
      // history re-read, so the "who changed it" / before-value history
      // reflects the new change (F02 R28/R29).
      void queryClient.invalidateQueries({
        queryKey: keys.notificationPreferences.forUser(userId),
      });
    },
  });
}

/*
 * On-behalf update mutation (F02 R27, AC-7aaa025f) — the ADMINISTRATOR surface.
 * An administrator changes a user's choices on that user's behalf; the server
 * SCOPES the change to the target user's own organization and attributes it to
 * the ACTOR (the administrator, established server-side from the session — never
 * the browser). The mutation sends only the target id and the choices.
 *
 * Mutate → invalidate: on success the target user's preferences AND their change
 * history are invalidated, so the portal re-reads the new values and the
 * "who changed it" history shows the administrator's name on the change
 * (AC-bf8668ea). No optimistic update — like every write, mutate then
 * invalidate.
 *
 * The error is NOT swallowed: an out-of-scope target is refused by the server
 * with the byte-identical not-found (404, AC-7aaa025f) and an unknown key with
 * a whole-submission refusal (AC-8ddebd3b); both arrive as an `ApiError` the
 * admin surface treats as failed, never success, with nothing shown as stored.
 */
export function useUpdateNotificationPreferencesOnBehalf(userId: UserId) {
  const queryClient = useQueryClient();
  return useMutation<
    NotificationPreferences,
    Error,
    UpdateNotificationPreferencesInput
  >({
    mutationFn: (input) =>
      api.notificationPreferences.updateOnBehalf(userId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.notificationPreferences.forUser(userId),
      });
    },
  });
}
